import * as React from "react";

import {
  ParameterField,
  useParameterValue,
  type ParameterFieldParameter,
} from "@/shared/components/ResourceForm";
import {
  findPlaceholders,
  type TriggerTemplateCatalog,
} from "@/shared/domain/triggers";
import {
  Combobox,
  Field,
  Select,
  TextInput,
  type SelectItem,
} from "@/shared/foundation/primitives";

import styles from "./invocationSpec.module.css";

export interface ParameterRowProps {
  /**
   * Where the values live, a record by name: e.g. `targets.0.spec.parameters`
   * or `targets.0.variables`.
   */
  name: string;
  parameter: ParameterFieldParameter;
  /**
   * The paths a value may reference. Without one the row takes only a fixed
   * value, and a stored `{{ }}` value shows as the text it is.
   */
  templateCatalog?: TriggerTemplateCatalog;
  /** What is wrong with the value, e.g. a required one left empty. */
  error?: string;
}

type ValueSource = "literal" | "variable";

const SOURCE_ITEMS: readonly SelectItem<ValueSource>[] = [
  { value: "literal", label: "Fixed value" },
  { value: "variable", label: "Trigger variable" },
];

/** The path a value is exactly one placeholder for, or null. */
function placeholderPath(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  const found = findPlaceholders(trimmed);
  return found.length === 1 &&
    found[0].start === 0 &&
    found[0].end === trimmed.length
    ? found[0].name
    : null;
}

/**
 * One declared parameter of an action, or variable of an agent.
 *
 * An unset row shows the declared default as its placeholder; only a value
 * someone set is held by the form, so one left alone, or cleared, follows the
 * default whenever it runs. Given a template catalog, the value is either
 * fixed or a trigger variable, picked from the catalog's paths.
 */
export function ParameterRow({
  name,
  parameter,
  templateCatalog,
  error,
}: ParameterRowProps) {
  const { value, isSet, editable, displayText, write } = useParameterValue(
    name,
    parameter,
  );
  // Which control the row offers while nothing is written yet. A source the
  // person picked outlives an empty value, so the field does not flip back.
  const [picked, setPicked] = React.useState<ValueSource | null>(null);
  const variablePath = placeholderPath(value);
  const offersVariables = templateCatalog !== undefined && editable;
  const source: ValueSource = offersVariables
    ? picked ?? (variablePath === null ? "literal" : "variable")
    : "literal";

  const variablePaths: readonly string[] = (templateCatalog?.paths ?? [])
    .filter((entry) => !entry.container)
    .map((entry) => entry.path);

  return (
    <ParameterField.Root
      parameter={parameter}
      error={error}
      isSet={isSet}
      data-source={source}
    >
      <ParameterField.Header />
      <ParameterField.Description />
      <ParameterField.Inputs>
        {offersVariables && (
          <Select<ValueSource>
            items={SOURCE_ITEMS}
            value={source}
            onValueChange={(next) => {
              setPicked(next);
              // The old value cannot mean anything under the new source, so
              // the row goes back to showing the declared default.
              write(undefined);
            }}
            aria-label={`Value source for ${parameter.name}`}
            size="lg"
            className={styles.parameterSource}
          />
        )}
        <ParameterField.Value>
          {source === "variable" ? (
            <Combobox<string>
              items={variablePaths}
              value={variablePath}
              onValueChange={(path) => {
                write(path === null ? "" : `{{ ${path} }}`);
              }}
              autoHighlight
              size="lg"
            >
              <Combobox.TriggerInput
                placeholder="Search trigger variables"
                aria-label={`Variable for ${parameter.name}`}
              />
              <Combobox.Content>
                <Combobox.Empty />
                <Combobox.List>
                  {(path: string) => <Combobox.Item key={path} value={path} />}
                </Combobox.List>
              </Combobox.Content>
            </Combobox>
          ) : (
            <Field.Control
              render={<TextInput spellCheck={false} size="lg" />}
              value={isSet ? displayText : ""}
              placeholder={isSet ? undefined : displayText}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                write(
                  event.target.value === "" ? undefined : event.target.value,
                );
              }}
              readOnly={!editable}
              aria-label={parameter.name}
            />
          )}
        </ParameterField.Value>
      </ParameterField.Inputs>
      <ParameterField.Error />
    </ParameterField.Root>
  );
}
